import { useMutation, useQuery } from '@tanstack/react-query';

import {
  getViolations,
  addViolationContact,
  updateViolationContact,
  deleteViolationContact,
  updateViolationStatus,
  updateViolationOpinion,
  addViolationComment,
  updateViolationComment,
  deleteViolationComment,
  getViolationById,
  getViolationsStatistics
} from '@/api/violations';
import { Violations } from '@/models/violations';

type UseViolationsParams = {
  params?: Violations.ViolationParams;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  enabled?: boolean;
};

type MutationParams<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
  onSettled?: (data: T | undefined, error: any) => void;
};

export const useViolations = ({
  params,
  onSuccess,
  onError,
  enabled = true
}: UseViolationsParams) =>
  useQuery({
    queryKey: ['violations', params],
    queryFn: async () => {
      const result = await getViolations(params);
      return result.data;
    },
    placeholderData: (previousData) => previousData,
    enabled,
    meta: {
      onSuccess,
      onError
    }
  });

type UseViolationParams = {
  postId: number;
  params?: Violations.ViolationParams;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  enabled?: boolean;
};

export const useViolation = ({
  postId,
  params,
  onSuccess,
  onError,
  enabled = true
}: UseViolationParams) =>
  useQuery({
    queryKey: ['violation', postId, params],
    queryFn: async () => {
      const result = await getViolationById(postId, params);
      return result.data;
    },
    enabled,
    meta: {
      onSuccess,
      onError
    }
  });

// Contact hooks
export const useAddViolationContact = ({
  onSuccess,
  onError
}: MutationParams<Violations.Contacted> = {}) =>
  useMutation({
    mutationKey: ['addContact'],
    mutationFn: async ({
      postId,
      data
    }: {
      postId: number;
      data: Pick<Violations.Contacted, 'contactedAt'>;
    }) => {
      const result = await addViolationContact(postId, data);
      return result.data;
    },
    onSuccess,
    onError
  });

export const useUpdateViolationContact = ({
  onSuccess,
  onError
}: MutationParams<Violations.Contacted> = {}) =>
  useMutation({
    mutationKey: ['updateContact'],
    mutationFn: async ({
      postId,
      contactId,
      data
    }: {
      postId: number;
      contactId: number;
      data: Pick<Violations.Contacted, 'contactedAt'>;
    }) => {
      const result = await updateViolationContact(postId, contactId, data);
      return result.data;
    },
    onSuccess,
    onError
  });

export const useDeleteViolationContact = ({
  onSuccess,
  onError
}: MutationParams<void> = {}) =>
  useMutation({
    mutationKey: ['deleteContact'],
    mutationFn: async ({
      postId,
      contactId
    }: {
      postId: number;
      contactId: number;
    }) => {
      await deleteViolationContact(postId, contactId);
    },
    onSuccess,
    onError
  });

// Status hook
export const useUpdateViolationStatus = ({
  onSuccess,
  onError
}: MutationParams<Violations.Status> = {}) =>
  useMutation({
    mutationFn: async ({
      postId,
      data
    }: {
      postId: number;
      data: { status: Violations.StatusStage; comment?: string };
    }) => {
      const result = await updateViolationStatus(postId, data);
      return result.data;
    },
    onSuccess,
    onError
  });

// Opinion hook
export const useUpdateViolationOpinion = ({
  onSuccess,
  onError
}: MutationParams<Violations.SecondOpinion> = {}) =>
  useMutation({
    mutationKey: ['updateOpinion'],
    mutationFn: async ({
      postId,
      data
    }: {
      postId: number;
      data: Pick<Violations.SecondOpinion, 'opinion'>;
    }) => {
      const result = await updateViolationOpinion(postId, data);
      return result.data;
    },
    onSuccess,
    onError
  });

// Comment hooks
export const useAddViolationComment = ({
  onSuccess,
  onError,
  onSettled
}: MutationParams<Violations.Comment> = {}) =>
  useMutation({
    mutationFn: async ({
      postId,
      data
    }: {
      postId: number;
      data: { comment: string };
    }) => {
      const result = await addViolationComment(postId, data);
      return result.data;
    },
    onSuccess,
    onError,
    onSettled
  });

export const useUpdateViolationComment = ({
  onSuccess,
  onError
}: MutationParams<Violations.Comment> = {}) =>
  useMutation({
    mutationFn: async ({
      postId,
      commentId,
      data
    }: {
      postId: number;
      commentId: number;
      data: { comment: string };
    }) => {
      const result = await updateViolationComment(postId, commentId, data);
      return result.data;
    },
    onSuccess,
    onError
  });

export const useDeleteViolationComment = ({
  onSuccess,
  onError
}: MutationParams<void> = {}) =>
  useMutation({
    mutationFn: async ({
      postId,
      commentId
    }: {
      postId: number;
      commentId: number;
    }) => {
      await deleteViolationComment(postId, commentId);
    },
    onSuccess,
    onError
  });

export const useViolationsStatistics = ({
  onSuccess,
  onError,
  enabled = true
}: {
  onSuccess?: (data: Violations.Statistics) => void;
  onError?: (error: any) => void;
  enabled?: boolean;
} = {}) =>
  useQuery({
    queryKey: ['violationsStatistics'],
    queryFn: async () => {
      const result = await getViolationsStatistics();
      return result.data;
    },
    enabled,
    meta: {
      onSuccess,
      onError
    }
  });
